import { useMemo, useState } from "react";
import styles from "./FAQManager.module.css";
import {
  Plus,
  GripVertical,
  Pencil,
  Trash2,
  FolderOpen,
  Search,
  X,
  Filter,
} from "lucide-react";
import ToggleSwitch from "../../../components/BaseComponents/Toggle/ToggleSwitch";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import AddEditFAQModal from "../../../components/Faq/AddEditFAQModal";
import FaqCategoriesModal from "../../../components/Faq/FaqCategoriesModal";
import { useToast } from "../../../context/ToastContext";
import {
  useFaqsList,
  useCreateFaq,
  useUpdateFaq,
  useDeleteFaq,
} from "../../../api/cms/useFaqApi";
import { useFaqCategoriesList } from "../../../api/cms/useFaqCategoryApi";

const FAQManager = () => {
  const { showToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selected, setSelected] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [query, setQuery] = useState("");
  // "" = every category, "none" = the ones filed under no category at all.
  const [categoryId, setCategoryId] = useState("");

  // No limit passed: the hook pages through the lot. Asking for 100 here is
  // what hid 34 of the 134 questions.
  const { data: faqsRaw, total, loading, error, refetch } = useFaqsList();
  const { data: categoriesRaw, refetch: refetchCategories } = useFaqCategoriesList();
  // Memoised because the search below filters off it: a fresh [] on every
  // render would re-run the filter on every keystroke anywhere on the page.
  const faqs = useMemo(() => (Array.isArray(faqsRaw) ? faqsRaw : []), [faqsRaw]);
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];
  const createFaq = useCreateFaq();
  const updateFaq = useUpdateFaq();
  const deleteFaq = useDeleteFaq();

  const openAdd = () => {
    setMode("add");
    setSelected(null);
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setMode("edit");
    setSelected(faq);
    setShowModal(true);
  };

  const handleToggleStatus = async (faq) => {
    const newStatus = faq.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await updateFaq.execute(faq.id, { status: newStatus });
      refetch();
      showToast("FAQ status updated successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update status", "error");
    }
  };

  const handleModalSubmit = async (payload) => {
    try {
      if (mode === "add") {
        await createFaq.execute(payload);
        showToast("FAQ created successfully", "success");
      } else if (selected?.id) {
        await updateFaq.execute(selected.id, payload);
        showToast("FAQ updated successfully", "success");
      }
      refetch();
      refetchCategories();
      setShowModal(false);
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to save FAQ", "error");
    }
  };

  const handleConfirmDeleteFaq = async () => {
    if (!faqToDelete) return;
    try {
      await deleteFaq.execute(faqToDelete.id);
      refetch();
      setFaqToDelete(null);
      showToast("FAQ deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete FAQ", "error");
      throw err;
    }
  };

  /* The list is one long unpaginated page, so finding "the Qatar one" means
     scrolling past everything else. Searching covers the question, the answer
     behind it and the category it sits in — an answer is often the only place
     a destination or a supplier name is written down.

     The category picker is the other half of that: search is for a question
     you can half remember, the picker is for "show me everything filed under
     Betalingen", which is how a FAQ this size is actually worked through.
     They combine.

     Filtering never renumbers: the number on a row is the FAQ's place in the
     full list, which is what the ordering is about, so it stays put whether
     the row is being searched for or scrolled to. */
  const q = query.trim().toLowerCase();

  /* Picking a MAIN category includes the questions filed under its
     subcategories. On the website a subcategory is a filter inside its
     parent's card, so "Voor de reis" means that whole card — picking it and
     seeing only the handful filed directly on the parent would read as if the
     subcategories were empty. Picking a subcategory means just that one. */
  const filterIds = useMemo(() => {
    if (!categoryId || categoryId === "none") return null;
    const id = Number(categoryId);
    const children = categories
      .filter((c) => Number(c.parentId) === id)
      .map((c) => Number(c.id));
    return new Set([id, ...children]);
  }, [categoryId, categories]);

  const visibleFaqs = useMemo(() => {
    const rows = faqs.map((faq, index) => ({ faq, position: index + 1 }));
    return rows.filter(({ faq }) => {
      if (categoryId === "none" && faq.faqCategoryId != null) return false;
      if (filterIds && !filterIds.has(Number(faq.faqCategoryId))) return false;
      if (!q) return true;
      return [faq.question, faq.shortAnswer, faq.answer, faq.faqCategory?.title]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [faqs, q, categoryId, filterIds]);

  /* Main categories with their subcategories indented underneath, each with
     how many questions it holds. The count is half the answer to "where did
     that one go?", and a category reading (0) is worth seeing before you go
     looking inside it. */
  const categoryOptions = useMemo(() => {
    const nameOf = (c) => c?.title?.trim() || `Category #${c?.id}`;
    const countIn = (ids) => faqs.filter((f) => ids.has(Number(f.faqCategoryId))).length;
    const options = [];

    for (const parent of categories.filter((c) => !c.parentId)) {
      const children = categories.filter((c) => Number(c.parentId) === Number(parent.id));
      options.push({
        id: String(parent.id),
        label: nameOf(parent),
        indented: false,
        count: countIn(new Set([Number(parent.id), ...children.map((c) => Number(c.id))])),
      });
      for (const child of children) {
        options.push({
          id: String(child.id),
          label: nameOf(child),
          indented: true,
          count: countIn(new Set([Number(child.id)])),
        });
      }
    }

    // A subcategory whose parent was deleted still holds questions, so it is
    // still somewhere they can be hiding.
    for (const orphan of categories.filter(
      (c) => c.parentId && !categories.some((p) => Number(p.id) === Number(c.parentId))
    )) {
      options.push({
        id: String(orphan.id),
        label: nameOf(orphan),
        indented: false,
        count: countIn(new Set([Number(orphan.id)])),
      });
    }

    return options;
  }, [categories, faqs]);

  const uncategorised = faqs.filter((f) => f.faqCategoryId == null).length;
  // The hook reports the server's total; the mocked hook in the tests does
  // not, so fall back to what is actually in hand.
  const knownTotal = total || faqs.length;
  const filtering = Boolean(q || categoryId);
  const activeCategoryLabel =
    categoryId === "none"
      ? "without a category"
      : categoryOptions.find((o) => o.id === categoryId)?.label;

  const clearFilters = () => {
    setQuery("");
    setCategoryId("");
  };

  /* Which filter is on changes what the count can honestly say: rows "match"
     a search, but they are not matching anything when a category is doing the
     filtering. The total is the server's, so a filtered count is always out
     of everything there is, never out of a page of it. */
  const plural = knownTotal !== 1 ? "s" : "";
  const countLabel = !filtering
    ? `${knownTotal} FAQ${plural}`
    : q && activeCategoryLabel
      ? `${visibleFaqs.length} of ${knownTotal} FAQ${plural} in “${activeCategoryLabel}” match “${query.trim()}”`
      : q
        ? `${visibleFaqs.length} of ${knownTotal} FAQ${plural} match “${query.trim()}”`
        : `${visibleFaqs.length} of ${knownTotal} FAQ${plural} in “${activeCategoryLabel}”`;

  const submitting = createFaq.loading || updateFaq.loading;
  const apiError = error || createFaq.error || updateFaq.error || deleteFaq.error;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>FAQ Management</h1>
          <p>Manage questions and answers across the site</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={() => setShowCategoriesModal(true)}
          >
            <FolderOpen size={14} /> Categories
          </button>
          <button className={styles.primaryBtn} onClick={openAdd}>
            <Plus size={14} /> Create FAQ
          </button>
        </div>
      </div>

      {apiError && (
        <div
          className={styles.card}
          style={{
            marginBottom: 16,
            background: "#fef2f2",
            border: "1px solid #fecaca",
          }}
        >
          <p style={{ margin: 0, color: "#b91c1c" }}>{apiError}</p>
        </div>
      )}

      <div className={styles.filterBar}>
        <div className={styles.searchBar}>
          <Search size={15} className={styles.searchIcon} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search questions, answers or categories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search FAQs"
          />
          {query && (
            <button
              type="button"
              className={styles.clearSearch}
              onClick={() => setQuery("")}
              title="Clear search"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.categoryFilter}>
          <Filter size={14} className={styles.filterIcon} />
          <select
            className={styles.filterSelect}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="">All categories ({knownTotal})</option>
            {/* A subcategory is indented with spaces rather than CSS: an
                option does not take padding in every browser. */}
            {categoryOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.indented ? `\u00A0\u00A0\u00A0${o.label}` : o.label} ({o.count})
              </option>
            ))}
            {uncategorised > 0 && (
              <option value="none">Without a category ({uncategorised})</option>
            )}
          </select>
        </div>

        {filtering && (
          <button type="button" className={styles.clearFiltersBtn} onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      <div className={styles.tableSection}>
        <div className={styles.card}>
          {loading ? (
            <p className={styles.loadingText}>Loading FAQs…</p>
          ) : !faqs.length ? (
            <p className={styles.loadingText}>No FAQs yet. Create one to get started.</p>
          ) : !visibleFaqs.length ? (
            <p className={styles.loadingText}>
              {q && activeCategoryLabel
                ? `No FAQs in “${activeCategoryLabel}” match “${query.trim()}”.`
                : q
                  ? `No FAQs match “${query.trim()}”.`
                  : `Nothing filed under “${activeCategoryLabel}” yet.`}{" "}
              <button type="button" className={styles.linkBtn} onClick={clearFilters}>
                {q && activeCategoryLabel
                  ? "Clear the filters"
                  : q
                    ? "Clear the search"
                    : "Show all categories"}
              </button>
            </p>
          ) : (
            visibleFaqs.map(({ faq, position }) => (
            <div key={faq.id} className={styles.row}>
              <div className={styles.drag}>
                <GripVertical size={14} />
                {position}
              </div>

              <div className={styles.question}>{faq.question}</div>

              <div className={styles.scope}>
                {faq.faqCategory?.title || "—"}
              </div>

              <ToggleSwitch
                checked={faq.status === "ACTIVE"}
                onChange={() => handleToggleStatus(faq)}
              />

              <div className={styles.actions}>
                <button
                  className={styles.editBtn}
                  onClick={() => openEdit(faq)}
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  className={styles.deleteBtn}
                  onClick={() => setFaqToDelete(faq)}
                  title="Delete"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            ))
          )}
        </div>

        {!loading && (
          <div className={styles.footerBar}>
            <span className={styles.footerCount}>{countLabel}</span>
          </div>
        )}
      </div>

      {/* The category manager opens OVER this dialog rather than instead of it,
          so the question being edited keeps everything typed into it and a
          subcategory created there drops straight into the picker. */}
      <AddEditFAQModal
        open={showModal}
        onClose={() => setShowModal(false)}
        mode={mode}
        initialData={selected}
        categories={categories}
        onManageCategories={() => setShowCategoriesModal(true)}
        onSubmit={handleModalSubmit}
        submitting={submitting}
      />

      <FaqCategoriesModal
        open={showCategoriesModal}
        onClose={() => setShowCategoriesModal(false)}
        onCategoryCreated={refetchCategories}
        faqs={faqs}
      />

      <DeleteConfirmModal
        open={!!faqToDelete}
        onClose={() => setFaqToDelete(null)}
        title="Delete FAQ"
        itemName={faqToDelete?.question}
        onConfirm={handleConfirmDeleteFaq}
        loading={deleteFaq.loading}
      />
    </div>
  );
};

export default FAQManager;
